import {AppUrls} from 'shared/config/model/urls';
import {Pages} from 'shared/config/model/pages';

import React, {useState} from 'react';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';

import {IColumn} from 'shared/ui/Table/Table';
import SelectAllCheckbox from 'shared/ui/Checkbox/SelectAllCheckbox/SelectAllCheckbox';
import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import ListFiltersModal from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import useOpenState from 'shared/lib/hooks/useOpenState';
import {MassOperationsBlock, TagsBar} from 'entities/monitor';
import {useCreateGate} from 'entities/tariff';
import {ExceededModal} from 'entities/user';
import PlugEmpty from 'shared/ui/Plugs/PlugEmpty/PlugEmpty';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';
import ServerAlertRuleModal from 'features/server/ui/ServerAlertRule/ServerAlertRuleModal';
import {getServerBatchModalHeader, getServerMassOperationActions} from 'features/server/lib/serverBatchHelper';
import useServers from 'features/server/model/useServers';
import {SERVER_ALERT_RULE_METRICS} from 'features/server/model/ServerAlertRule/IServerAlertRule';

import CreateServerButton from '../CreateServerButton/CreateServerButton';
import ServerBatchOperationsForm from '../ServerBatchOperationsForm/ServerBatchOperationsForm';
import ServerRow from '../ServerRow/ServerRow';
import ServersAlertRulesHint from '../ServersAlertRulesHint/ServersAlertRulesHint';
import ServersFiltersForm from '../ServersFiltersForm/ServersFiltersForm';
import ServerModal from '../ServerModal';

const METRICS_COUNT = SERVER_ALERT_RULE_METRICS.length;

const Servers: React.FC = () => {
    const {
        pagination, isLoading, isFetching, refetch, search, onSearch, sort, setSort, serversCount, isEmpty, hasServers,
        list, maxServers, alertRulesLimit, hintVariant, canCreate, canCreateAlertRule,
    } = useServers();
    const {plugState, hasSelectedItems} = list;
    const {open: handleCreate, close: closeCreateGate, isCreateOpen, isExceededOpen} = useCreateGate(canCreate);
    const [isAlertRuleOpen, setIsAlertRuleOpen] = useState(false);
    const filtersModal = useOpenState();

    const columns: IColumn[] = [
        {
            label: <SelectAllCheckbox
                onChange={(e) => list.handleSelectAll(e.target.checked)}
                checked={list.isSelectAllChecked}
            />,
        },
        {label: t`Название`, sort: 'name'},
        {label: t`Состояние`, sort: 'status'},
        {label: t`Хост`, sort: 'hostname'},
        {label: t`ОС`, sort: 'os'},
        {label: t`Агент`},
        {label: t`Ядра CPU`, sort: 'cpu_cores'},
        {label: t`RAM`},
        {label: t`SWAP`},
        {label: t`Активность`, sort: 'last_seen_at'},
        {label: t`Хранение`},
        {label: t`Статус`},
        {label: ''},
    ];

    return (
        <ListPage
            documentTitle={t`Серверы | Overseer`}
            toolbar={
                <ListToolbar
                    title={i18n._(Pages.SERVERS)}
                    count={serversCount ?? 0}
                    maxCount={maxServers}
                    backUrl={AppUrls.HOME}
                    createButton={<CreateServerButton serversCount={serversCount} disabled={hasSelectedItems}/>}
                    hideCreateButtonOnMobile={isEmpty}
                    actions={
                        <TableToolbar
                            onSearch={onSearch}
                            searchValue={search}
                            onFilter={filtersModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                            disabled={hasSelectedItems}
                        />
                    }
                >
                    <TagsBar disabled={hasSelectedItems}/>
                </ListToolbar>
            }
            actions={hasServers && (
                <MassOperationsBlock
                    list={list}
                    actions={getServerMassOperationActions()}
                    getHeader={getServerBatchModalHeader}
                    renderForm={({action, onSuccess, handleClose}) => (
                        <ServerBatchOperationsForm
                            action={action}
                            onSuccess={onSuccess}
                            handleClose={handleClose}
                        />
                    )}
                >
                    {hintVariant && (
                        <ServersAlertRulesHint
                            variant={hintVariant}
                            rulesLimit={alertRulesLimit}
                            metricsCount={METRICS_COUNT}
                            onCreateRule={() => setIsAlertRuleOpen(true)}
                        />
                    )}
                </MassOperationsBlock>
            )}
            plug={
                <>
                    {plugState === 'noAccess' && <PlugNoAccess/>}
                    {plugState === 'empty' && <PlugEmpty onCreate={handleCreate} buttonText={t`Добавить сервер`}/>}
                </>
            }
            columns={columns}
            items={pagination?.items}
            rowFactory={(item) => (
                <ServerRow
                    key={item.uuid}
                    item={item}
                    canCreate={canCreate}
                    hasSelectedItems={hasSelectedItems}
                    isMassOperationsMode={list.isMassOperationsMode}
                />
            )}
            isLoading={isLoading}
            pagination={pagination}
            sort={sort}
            setSort={setSort}
            emptyStateComponent={plugState === 'notFound' ? t`Ничего не найдено` : true}
        >
            <ExceededModal
                isOpen={isExceededOpen}
                handleClose={closeCreateGate}
                type="servers"
            />
            <ServerModal
                open={isCreateOpen}
                handleClose={closeCreateGate}
            />
            <ListFiltersModal
                open={filtersModal.isOpen}
                handleClose={filtersModal.close}
                Form={ServersFiltersForm}
            />
            {canCreateAlertRule
                ? (
                    <ServerAlertRuleModal
                        open={isAlertRuleOpen}
                        handleClose={() => setIsAlertRuleOpen(false)}
                    />
                )
                : (
                    <ExceededModal
                        isOpen={isAlertRuleOpen}
                        handleClose={() => setIsAlertRuleOpen(false)}
                        type="alert_rules"
                    />
                )}
        </ListPage>
    );
};

export default Servers;
